import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Skeleton,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
/* eslint-disable camelcase */
import _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  ValleyBankLoanAccountWithCollateralAndStats,
  FinleyValleyBankResponseTapeRow,
  FinleyLoanParty,
  typeguards as valleyBankTypeguards,
  orderLoanAccountsLoans,
} from '@finley-technologies/valley-bank-utils';

import {
  useAddToValleyBankAssignmentQueueMutation,
  useGetUserQuery,
  useGetValleyBankAssignmentQueueQuery,
  ValleyBankAddToAssignmentQueueGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { Alert } from 'packages/common-frontend/components/Alert';
import { CsvXlsxDownloadSelect } from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import { generateAssignmentUrl } from 'valleyBank/domains/Assignment/ui/Assignment';
import { CustomerSummaryLink } from 'valleyBank/domains/Customers/hooks/CustomerContext';
import { LoansTable } from 'valleyBank/domains/LoanAccounts/ui/LoansTable';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';

const getCustomerFromReceivable = (
  receivables: FinleyValleyBankResponseTapeRow[],
  loanParties: FinleyLoanParty[],
): FinleyLoanParty | null => {
  const cifnum = receivables[0]?.cif_num;
  if (!cifnum) return null;
  return loanParties.find((party) => party.cif_num === cifnum) || null;
};

const loanPartiesDataKeyToHeaderMap = {
  cif_num: 'CIF Number',
  full_name: 'Customer',
  rel_code_description: 'Customer Type',
};

const collateralAccountDataKeyToHeaderMap = {
  accountNumber: 'Collateral',
  productName: 'Product Name',
  cifNum: 'CIF Number',
  fullName: 'Customer',
  loanPartyType: 'Loan Party Type',
  marketValue: 'Collateral Market Value',
  collateralAccountEffectiveAdvanceRate: 'Blended Advance Rate',
  preAllocationLendableValue: 'Pre Allocation Lendable Value',
  effectiveAllocation: 'Blended Allocation',
  postAllocationLendableValue: 'Post Allocation Lendable Value',
};

type UnassignModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onUnassign: () => void;
  collateralAccountNum: string;
  loanAccountNum: string;
};
const UnassignModal = ({
  isOpen,
  onCancel,
  onUnassign,
  collateralAccountNum,
  loanAccountNum,
}: UnassignModalProps) => {
  const cancelRef = React.useRef(null);
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Confirm Unassignment
          </AlertDialogHeader>

          <AlertDialogBody>
            <>
              <Text
                mb={2}
              >{`Are you sure you want to unassign ${collateralAccountNum} from ${loanAccountNum}?`}</Text>
              <Box py="12px" alignSelf="flex-end">
                <HStack spacing="25px">
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme={buttonColorScheme}
                    onClick={() => onCancel()}
                    px="20px"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme={buttonColorScheme}
                    onClick={() => {
                      onUnassign();
                    }}
                    px="20px"
                  >
                    Unassign
                  </Button>
                </HStack>
              </Box>
            </>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

// @jendevelops TODO: remove optional prop when fetching in component
type LoanSummaryProps = {
  loanAccount: ValleyBankLoanAccountWithCollateralAndStats;
  canUnassign?: boolean;
};
export const LoanSummary = ({ loanAccount, canUnassign }: LoanSummaryProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const {
    data: assignmentQueueData,
    refetch: refetchAssignmentQueue,
  } = useGetValleyBankAssignmentQueueQuery({
    fetchPolicy: 'network-only',
  });

  const [
    addToValleyBankAssignmentQueueMutation,
  ] = useAddToValleyBankAssignmentQueueMutation({
    fetchPolicy: 'network-only',
    onCompleted(data) {
      // mutation returns back a list of errors
      // surface any errors here
      if (data.valleyBankAddToAssignmentsQueue.length > 0) {
        if (!toast.isActive(errorToastId)) {
          toast({
            ...errorToastConfig,
            title: 'Errors on Unassignment',
            description: data.valleyBankAddToAssignmentsQueue.join('; '),
          });
        }
      } else {
        toast({
          ...successToastConfig,
          title: 'Successfully add unassignment to queue.',
        });
      }
    },
  });

  const [
    unassignment,
    setUnassignment,
  ] = useState<ValleyBankAddToAssignmentQueueGQLType>();

  if (!userData || !userData.user || !assignmentQueueData) {
    return <Skeleton width="full" />;
  }

  const {
    loanAccountNumber,
    principalBalance,
    totalMarketValue,
    effectiveAllocationRate,
    totalPostAllocationLendableValue,
    effectiveAdvanceRate,
    totalLendableValue,
    marginAvailable,
    marginAvailabilityRate,
    collateralAccounts,
    loanParties,
  } = loanAccount;

  const userId = userData.user.id;

  const sortedLoanParties = loanParties.sort((a, b) =>
    a.rel_code_description < b.rel_code_description ? -1 : 1,
  );

  const loanPartiesHeaders = _.map(
    loanPartiesDataKeyToHeaderMap,
    (header, key) => ({ label: header, key }),
  );

  const sortedCollateralAccounts = collateralAccounts.sort((a, b) => {
    const customerA = getCustomerFromReceivable(a.receivables, loanParties);
    const customerB = getCustomerFromReceivable(b.receivables, loanParties);

    if (customerA && customerB) {
      return customerA.rel_code < customerB.rel_code ? -1 : 1;
    }
    if (customerA) {
      return -1;
    }
    return 1;
  });

  const collateralAccountsTableData: {
    accountNumber: string;
    productName: string;
    cifNum: string;
    fullName: string;
    loanPartyType: string | undefined;
    marketValue: number;
    collateralAccountEffectiveAdvanceRate: number;
    preAllocationLendableValue: number;
    effectiveAllocation: number;
    postAllocationLendableValue: number;
    collateralAccountType: any;
    duplicateRecord: boolean;
  }[] = sortedCollateralAccounts.map(
    ({
      accountNumber,
      cifNum,
      owner,
      marketValue,
      effectiveAdvanceRate: collateralAccountEffectiveAdvanceRate,
      effectiveAllocation,
      productName,
      preAllocationLendableValue,
      postAllocationLendableValue,
      receivables,
    }) => {
      const customer = getCustomerFromReceivable(receivables, loanParties);

      const fullName = customer?.full_name || owner?.full_name || '-';

      const baseFields = receivables[0].receivableReponseTapeRow.baseFields;
      finleyAssert(
        valleyBankTypeguards.hasValleyBankRequiredBaseFields(baseFields),
        'Must be valid required base fields.',
      );
      const collateralAccountType = baseFields.collateral_account_type;

      const duplicateRecord = assignmentQueueData.valleyBankAssignmentQueue.find(
        (q) =>
          q.collateralAccountNumber === accountNumber &&
          q.loanAccountNumber === loanAccountNumber &&
          q.collateralAccountType === collateralAccountType &&
          q.action === 'unassign',
      );

      return {
        accountNumber,
        productName,
        cifNum,
        fullName,
        loanPartyType: customer?.rel_code_description,
        marketValue,
        collateralAccountEffectiveAdvanceRate,
        preAllocationLendableValue,
        effectiveAllocation,
        postAllocationLendableValue,
        collateralAccountType,
        duplicateRecord: !!duplicateRecord,
      };
    },
  );

  const collateralDataHeaders = _.map(
    collateralAccountDataKeyToHeaderMap,
    (header, key) => ({ label: header, key }),
  );

  return (
    <VStack w="100%" spacing="24px" alignItems="flex-start">
      <Flex gridRowGap="24px" gridColumnGap="24px" w="100%" flexWrap="wrap">
        <InfoCard
          title="Outstanding Balance"
          value={formatCurrencyAmount(principalBalance, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Market Value"
          value={formatCurrencyAmount(totalMarketValue, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Blended Advance Rate"
          value={percentageToPercentageLabel(effectiveAdvanceRate * 100, 2)}
          icon={<Icon icon="percentage" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Pre Allocation Lending Value"
          value={formatCurrencyAmount(totalLendableValue, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Blended Allocation"
          value={percentageToPercentageLabel(effectiveAllocationRate * 100, 2)}
          icon={<Icon icon="percentage" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Collateral Post Allocation Lending Value"
          value={formatCurrencyAmount(
            totalPostAllocationLendableValue,
            Bank.Currency.USD,
          )}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Margin Availability"
          value={formatCurrencyAmount(marginAvailable, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          w="400px"
        />
        <InfoCard
          title="Margin Availability Rate"
          value={percentageToPercentageLabel(marginAvailabilityRate * 100, 2)}
          icon={<Icon icon="percentage" size="24px" />}
          w="400px"
        />
      </Flex>

      <Heading
        as="h2"
        fontSize="24px"
        mb={loanParties.length === 0 ? '24px' : '8px'}
      >
        Loans Parties
      </Heading>
      {loanParties.length === 0 && (
        <Alert status="info">
          <Text>No customers are associated with this loan account</Text>
        </Alert>
      )}
      {loanParties.length > 0 && (
        <CsvXlsxDownloadSelect
          dataRows={sortedLoanParties}
          headers={loanPartiesHeaders}
          filenamePrefix={`loan-account-${loanAccountNumber}-loan-parties`}
        />
      )}
      {loanParties.length > 0 && (
        <Table>
          <Thead>
            <Tr>
              {_.map(loanPartiesDataKeyToHeaderMap, (header) => (
                <Th>{header}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {sortedLoanParties.map(
              ({ cif_num, rel_code_description, full_name }) => (
                <Tr key={cif_num}>
                  <Td>
                    <CustomerSummaryLink cifnum={cif_num}>
                      {cif_num}
                    </CustomerSummaryLink>
                  </Td>
                  <Td>
                    <CustomerSummaryLink cifnum={cif_num}>
                      {full_name}
                    </CustomerSummaryLink>
                  </Td>
                  <Td>{rel_code_description}</Td>
                </Tr>
              ),
            )}
          </Tbody>
        </Table>
      )}
      <Heading
        as="h2"
        fontSize="24px"
        mb={loanParties.length === 0 ? '24px' : '8px'}
      >
        Assigned Collateral Accounts
      </Heading>
      {collateralAccounts.length === 0 && (
        // @jendevelops should this be an error status and throw and error if no customer associated with a loan account?
        <Alert status="info">
          <Text>No collateral accounts associated with this Loan Account</Text>
        </Alert>
      )}
      {collateralAccounts.length > 0 && (
        <CsvXlsxDownloadSelect
          dataRows={collateralAccountsTableData}
          headers={collateralDataHeaders}
          filenamePrefix={`collateral-accounts-loan-account-${loanAccountNumber}`}
        />
      )}
      {collateralAccounts.length > 0 && (
        <Table>
          <Thead>
            <Tr>
              {_.map(collateralAccountDataKeyToHeaderMap, (header) => (
                <Th>{header}</Th>
              ))}
              {canUnassign && <Th />}
            </Tr>
          </Thead>
          <Tbody>
            {collateralAccountsTableData.map(
              ({
                accountNumber,
                productName,
                cifNum,
                fullName,
                loanPartyType,
                marketValue,
                collateralAccountEffectiveAdvanceRate,
                preAllocationLendableValue,
                effectiveAllocation,
                postAllocationLendableValue,
                collateralAccountType,
                duplicateRecord,
              }) => {
                return (
                  <Tr key={accountNumber}>
                    {/* @jendevelops TODO add collateral page link */}
                    <Td>{accountNumber}</Td>
                    <Td>{productName}</Td>
                    {/* @jendevelops TODO add customer page link */}
                    <Td>
                      <CustomerSummaryLink cifnum={cifNum}>
                        {cifNum}
                      </CustomerSummaryLink>
                    </Td>
                    <Td>
                      <CustomerSummaryLink cifnum={cifNum}>
                        {fullName}
                      </CustomerSummaryLink>
                    </Td>
                    <Td>
                      {loanPartyType || (
                        <HStack w="100%" spacing="8px">
                          <Text>N/A</Text>
                          <Tooltip
                            label="Not a loan party of this loan"
                            borderRadius="8px"
                          >
                            <span
                              style={{
                                padding: '16px 4px',
                                verticalAlign: 'middle',
                              }}
                            >
                              <Icon icon="circle-info" />
                            </span>
                          </Tooltip>
                        </HStack>
                      )}
                    </Td>
                    <Td>
                      {/* Collateral Market Value */}
                      {formatCurrencyAmount(marketValue, Bank.Currency.USD)}
                    </Td>
                    <Td>
                      {/* Collateral Advance Rate */}
                      {/* @jenDevelops TODO account for currency */}
                      {percentageToPercentageLabel(
                        collateralAccountEffectiveAdvanceRate * 100,
                        2,
                      )}
                    </Td>
                    <Td>
                      {/* Pre Allocation Lendable Value */}
                      {formatCurrencyAmount(
                        preAllocationLendableValue,
                        Bank.Currency.USD,
                      )}
                    </Td>
                    <Td>
                      {/* Collateral Allocation */}
                      {percentageToPercentageLabel(
                        effectiveAllocation * 100,
                        2,
                      )}
                    </Td>
                    <Td>
                      {/* Post Allocation Lendable Value */}
                      {formatCurrencyAmount(
                        postAllocationLendableValue,
                        Bank.Currency.USD,
                      )}
                    </Td>
                    {canUnassign && (
                      <Td>
                        {duplicateRecord ? (
                          <Button
                            colorScheme="aurora"
                            onClick={() => {
                              navigate(generateAssignmentUrl(true));
                            }}
                          >
                            View Queue
                          </Button>
                        ) : (
                          <Button
                            colorScheme="aurora"
                            onClick={() => {
                              setUnassignment({
                                loanAccountNumber,
                                collateralAccountNumber: accountNumber,
                                collateralAccountType,
                                action: 'unassign',
                                userId,
                              });
                            }}
                          >
                            Unassign
                          </Button>
                        )}
                      </Td>
                    )}
                  </Tr>
                );
              },
            )}
          </Tbody>
        </Table>
      )}
      {unassignment !== undefined && (
        <UnassignModal
          isOpen={!!unassignment}
          onCancel={() => {
            setUnassignment(undefined);
          }}
          onUnassign={async () => {
            await addToValleyBankAssignmentQueueMutation({
              variables: {
                records: [unassignment],
              },
            });
            await refetchAssignmentQueue();
            setUnassignment(undefined);
          }}
          collateralAccountNum={unassignment.collateralAccountNumber}
          loanAccountNum={unassignment.loanAccountNumber}
        />
      )}
      {/* @jenDevelops use getLoans when piped in from other PR */}
      <LoansTable
        title="Loan Summary"
        noLoansMessage="There are no loans associated with this loan account."
        loans={orderLoanAccountsLoans([loanAccount])}
        showStats={false}
      />
    </VStack>
  );
};
